import { BreadcrumbSubBar } from '@components/BreadcrumbSubBar';
import { BasePage } from './bases/BasePage';
import { useInvoiceDatesQuery, useInvoiceJobsQuery } from '@features/redux/apis/cutState';
import { Box, Link, Typography, Select, MenuItem, Tooltip } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useStyles } from '@components/tables/DataTable/styles/DataTable.styles';
import { getPreviousMonth } from '@utils/dates';

const TODAY = new Date();

const getMonthName = (month: string) => new Date(1969, +month - 1).toLocaleString('en-us', { month: 'long' });

export const InvoiceManagement = () => {
    const classes = useStyles();

    const [selectedYear, setSelectedYear] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('');

    const { data: dates = [], isFetching: datesLoading } = useInvoiceDatesQuery({});
    const { data: jobs = [], isFetching: jobsLoading } = useInvoiceJobsQuery(
        {
            year: selectedYear,
            month: selectedMonth!,
        },
        { skip: !selectedYear || !selectedMonth }
    );

    const dateMap = useMemo(() => Object.fromEntries(dates), [dates]);

    // ? Auto-select the previous month
    useEffect(() => {
        if (datesLoading) return;

        const date = getPreviousMonth(TODAY);

        const yearString = date.toLocaleDateString('en-us', {
            year: '2-digit',
        });
        const monthString = date.toLocaleDateString('en-us', {
            month: '2-digit',
        });

        if (dateMap[yearString]) setSelectedYear(yearString);
        if (dateMap[yearString]?.includes(monthString)) setSelectedMonth(monthString);
    }, [datesLoading, dateMap]);

    return (
        <BasePage>
            <BreadcrumbSubBar />
            <div style={{ marginTop: '180px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Typography variant='h3' color='black' fontWeight='bold'>
                    Invoice Management
                </Typography>

                <Box display='flex' flexWrap='wrap' gap='10px'>
                    <Box display='flex' gap='10px' alignItems='center'>
                        <Typography variant='h3' height='fit-content'>
                            Year:
                        </Typography>

                        <Select
                            displayEmpty
                            // placeholder={datesLoading ? 'Loading...' : 'Year'}
                            disabled={datesLoading}
                            value={selectedYear}
                            onChange={({ target }) => {
                                setSelectedYear(target.value);
                                setSelectedMonth('');
                            }}
                            renderValue={(selected) => {
                                if (datesLoading) return 'Loading...';

                                if (!selected) return 'Year';

                                return `20${selected}`;
                            }}>
                            <MenuItem disabled value=''>
                                Year
                            </MenuItem>

                            {dates.map(([year]) => (
                                <MenuItem key={`select-year-${year}`} value={year}>
                                    20{year}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    <Box display='flex' gap='10px' alignItems='center'>
                        <Typography variant='h3' height='fit-content'>
                            Month:
                        </Typography>

                        <Select
                            displayEmpty
                            // placeholder={datesLoading ? 'Loading...' : 'Month'}
                            value={selectedMonth}
                            disabled={!selectedYear}
                            onChange={({ target }) => {
                                setSelectedMonth(target.value);
                            }}
                            renderValue={(selected) => {
                                if (datesLoading) return 'Loading...';

                                if (!selected) return 'Month';

                                return getMonthName(selected);
                            }}>
                            <MenuItem disabled value=''>
                                Month
                            </MenuItem>

                            {selectedYear &&
                                dateMap[selectedYear]?.map((month) => (
                                    <MenuItem key={`select-month-${month}`} value={month}>
                                        {getMonthName(month)}
                                    </MenuItem>
                                ))}
                        </Select>
                    </Box>
                </Box>

                <DataGridPro
                    autoHeight
                    loading={jobsLoading}
                    className={classes.table}
                    rows={selectedYear && selectedMonth ? jobs : []}
                    columns={[
                        {
                            headerName: 'File Name',
                            field: 'fileName',
                            flex: 1,
                            minWidth: 200,
                            sortable: false,
                            renderCell: ({ value, row }) => {
                                return (
                                    <Tooltip title='Download this file' placement='top' arrow>
                                        <Link download href={row.downloadUrl}>
                                            {value}
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            headerName: 'Last Modified',
                            field: 'lastModified',
                            minWidth: 150,
                            sortable: false,
                            valueFormatter: ({ value }) =>
                                new Date(value).toLocaleDateString('en-us', {
                                    month: 'numeric',
                                    day: 'numeric',
                                    year: '2-digit',
                                    hour: 'numeric',
                                    minute: 'numeric',
                                }),
                        },
                        {
                            headerName: 'Size',
                            field: 'size',
                            minWidth: 100,
                            sortable: false,
                        },
                    ]}
                    getRowId={({ fileName, size }) => `${fileName}-${size}`}
                    disableVirtualization
                    disableEval
                    disableColumnMenu
                    disableColumnFilter
                    disableColumnReorder
                    disableColumnResize
                    disableChildrenFiltering
                    disableChildrenSorting
                    disableMultipleRowSelection
                    disableRowSelectionOnClick
                    disableColumnSelector
                    disableMultipleColumnsSorting
                    disableDensitySelector
                    disableAutosize
                    disableColumnPinning
                    disableMultipleColumnsFiltering
                    hideFooter
                    hideFooterSelectedRowCount
                    hideFooterPagination
                    hideFooterRowCount
                />
            </div>
        </BasePage>
    );
};
